const dayCodes = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'];

function parseDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function startOfWeek(date) {
  const next = startOfDay(date);
  next.setDate(next.getDate() - next.getDay());
  return next;
}

function addTimeToDay(base, day) {
  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    base.getHours(),
    base.getMinutes(),
    base.getSeconds(),
    base.getMilliseconds()
  );
}

function monthDistance(from, to) {
  return to.getFullYear() * 12 + to.getMonth() - (from.getFullYear() * 12 + from.getMonth());
}

function readPart(parts, type) {
  return Number(parts.find((part) => part.type === type)?.value || 0);
}

function getZonedParts(date, timezone) {
  try {
    const parts = new Intl.DateTimeFormat('en-US-u-ca-gregory', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const year = readPart(parts, 'year');
    const month = readPart(parts, 'month');
    const day = readPart(parts, 'day');

    return {
      year,
      month,
      day,
      hours: readPart(parts, 'hour'),
      minutes: readPart(parts, 'minute'),
      seconds: readPart(parts, 'second'),
      milliseconds: date.getUTCMilliseconds(),
      weekdayIndex: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
    };
  } catch {
    return null;
  }
}

function parseDateKey(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const normalized = new Date(Date.UTC(year, month - 1, day));
  if (
    normalized.getUTCFullYear() !== year ||
    normalized.getUTCMonth() !== month - 1 ||
    normalized.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day, weekdayIndex: normalized.getUTCDay() };
}

function dateOnlyUtc(parts) {
  return Date.UTC(parts.year, parts.month - 1, parts.day);
}

function diffDays(left, right) {
  return Math.floor((dateOnlyUtc(left) - dateOnlyUtc(right)) / 86400000);
}

function diffMonths(left, right) {
  return left.year * 12 + left.month - (right.year * 12 + right.month);
}

function addDays(parts, days) {
  const next = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return {
    year: next.getUTCFullYear(),
    month: next.getUTCMonth() + 1,
    day: next.getUTCDate(),
    weekdayIndex: next.getUTCDay(),
  };
}

function startOfCalendarWeek(parts) {
  return addDays(parts, -parts.weekdayIndex);
}

function zonedOffsetMs(date, timezone) {
  const parts = getZonedParts(date, timezone);
  if (!parts) return null;
  return Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hours,
    parts.minutes,
    parts.seconds,
    parts.milliseconds
  ) - date.getTime();
}

function zonedPartsToDate(parts, timezone) {
  const localAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hours,
    parts.minutes,
    parts.seconds,
    parts.milliseconds
  );
  const firstOffset = zonedOffsetMs(new Date(localAsUtc), timezone);
  if (firstOffset === null) return null;
  const firstCandidate = new Date(localAsUtc - firstOffset);
  const secondOffset = zonedOffsetMs(firstCandidate, timezone);
  if (secondOffset === null) return null;
  return new Date(localAsUtc - secondOffset);
}

export function isRoutineTask(task) {
  return Boolean(
    task?.recurrence &&
      (task.scope === 'family' || task.scopeType === 'family') &&
      Array.isArray(task.tags) &&
      task.tags.some((tag) => String(tag).trim().toLowerCase() === 'routine')
  );
}

export function getRoutineTone(task) {
  return (
    (task?.tags || []).find((tag) =>
      ['gentle', 'important', 'playful'].includes(String(tag).toLowerCase())
    ) || 'gentle'
  );
}

export function getRoutineCategory(task) {
  return (
    (task?.tags || []).find((tag) =>
      ['kids', 'parents', 'pets'].includes(String(tag).toLowerCase())
    ) || 'family'
  );
}

export function formatRoutineRepeat(recurrence) {
  if (!recurrence?.frequency) return '';
  const interval = recurrence.interval || 1;

  if (recurrence.frequency === 'daily') {
    return interval === 1 ? 'Repeats every day' : `Repeats every ${interval} days`;
  }

  if (recurrence.frequency === 'weekly') {
    const days = (recurrence.daysOfWeek || []).map((day) => String(day).toUpperCase()).join(', ');
    const cadence = interval === 1 ? 'Repeats weekly' : `Repeats every ${interval} weeks`;
    return days ? `${cadence} on ${days}` : cadence;
  }

  if (recurrence.frequency === 'monthly') {
    return recurrence.dayOfMonth ? `Repeats monthly on day ${recurrence.dayOfMonth}` : 'Repeats monthly';
  }

  return recurrence.monthOfYear && recurrence.dayOfMonth
    ? `Repeats yearly on ${recurrence.monthOfYear}/${recurrence.dayOfMonth}`
    : 'Repeats yearly';
}

function recurrenceAllowsDate(recurrence, base, candidate) {
  const interval = Math.max(1, recurrence.interval || 1);
  const candidateDay = startOfDay(candidate);
  const baseDay = startOfDay(base);

  if (candidateDay < baseDay) return false;

  if (recurrence.endsAt) {
    const endsAt = parseDate(recurrence.endsAt);
    if (endsAt && addTimeToDay(base, candidateDay) > endsAt) return false;
  }

  if (recurrence.frequency === 'daily') {
    return Math.floor((candidateDay - baseDay) / 86400000) % interval === 0;
  }

  if (recurrence.frequency === 'weekly') {
    const allowedDays = recurrence.daysOfWeek?.length
      ? recurrence.daysOfWeek
      : [dayCodes[base.getDay()]];
    const diffWeeks = Math.floor((startOfWeek(candidateDay) - startOfWeek(baseDay)) / (7 * 86400000));
    return diffWeeks >= 0 && diffWeeks % interval === 0 && allowedDays.includes(dayCodes[candidateDay.getDay()]);
  }

  if (recurrence.frequency === 'monthly') {
    const diffMonths = monthDistance(baseDay, candidateDay);
    return diffMonths >= 0 && diffMonths % interval === 0 && candidateDay.getDate() === (recurrence.dayOfMonth || base.getDate());
  }

  const diffYears = candidateDay.getFullYear() - baseDay.getFullYear();
  return (
    diffYears >= 0 &&
    diffYears % interval === 0 &&
    candidateDay.getMonth() === (recurrence.monthOfYear || base.getMonth() + 1) - 1 &&
    candidateDay.getDate() === (recurrence.dayOfMonth || base.getDate())
  );
}

function recurrenceAllowsZonedDate(recurrence, base, candidate, timezone) {
  const interval = Math.max(1, recurrence.interval || 1);
  if (dateOnlyUtc(candidate) < dateOnlyUtc(base)) return false;

  if (recurrence.endsAt) {
    const occurrence = zonedPartsToDate({ ...base, ...candidate }, timezone);
    const endsAt = parseDate(recurrence.endsAt);
    if (occurrence && endsAt && occurrence > endsAt) return false;
  }

  if (recurrence.frequency === 'daily') {
    return diffDays(candidate, base) % interval === 0;
  }

  if (recurrence.frequency === 'weekly') {
    const allowedDays = recurrence.daysOfWeek?.length
      ? recurrence.daysOfWeek
      : [dayCodes[base.weekdayIndex]];
    const diffWeeks = Math.floor(diffDays(startOfCalendarWeek(candidate), startOfCalendarWeek(base)) / 7);
    return diffWeeks >= 0 && diffWeeks % interval === 0 && allowedDays.includes(dayCodes[candidate.weekdayIndex]);
  }

  if (recurrence.frequency === 'monthly') {
    const distance = diffMonths(candidate, base);
    return distance >= 0 && distance % interval === 0 && candidate.day === (recurrence.dayOfMonth || base.day);
  }

  const diffYears = candidate.year - base.year;
  return (
    diffYears >= 0 &&
    diffYears % interval === 0 &&
    candidate.month === (recurrence.monthOfYear || base.month) &&
    candidate.day === (recurrence.dayOfMonth || base.day)
  );
}

export function projectRoutineTaskForDay(task, dateKey) {
  if (!isRoutineTask(task) || !task.dueDate || !task.recurrence) return null;
  const timezone = typeof task.recurrence.timezone === 'string' && task.recurrence.timezone.trim()
    ? task.recurrence.timezone.trim()
    : null;

  if (timezone) {
    const baseDate = parseDate(task.dueDate);
    const base = baseDate ? getZonedParts(baseDate, timezone) : null;
    const candidate = parseDateKey(dateKey);
    if (!base || !candidate || !recurrenceAllowsZonedDate(task.recurrence, base, candidate, timezone)) return null;
    const occurrence = zonedPartsToDate({ ...base, ...candidate }, timezone);
    return occurrence ? { task, occurrenceAt: occurrence.toISOString() } : null;
  }

  const base = parseDate(task.dueDate);
  const candidate = parseDate(`${dateKey}T12:00:00`);
  if (!base || !candidate || !recurrenceAllowsDate(task.recurrence, base, candidate)) return null;

  return {
    task,
    occurrenceAt: addTimeToDay(base, candidate).toISOString(),
  };
}

export function projectRoutineTasksForDay(tasks, dateKey) {
  return (tasks || [])
    .filter((task) => ['open', 'in_progress'].includes(String(task?.status || 'open').toLowerCase()))
    .map((task) => projectRoutineTaskForDay(task, dateKey))
    .filter(Boolean)
    .sort((left, right) => String(left.occurrenceAt).localeCompare(String(right.occurrenceAt)));
}
